import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
} from '@leland-dev/leland-ui-library';
import React from 'react';

import type { Scalars } from '../../__generated-gql-types__/globalTypes';
import { Oops } from '../Oops';
import { Outcomes } from '../outcomes/Outcomes';

import { CoachReviewInfo } from './CoachReviewInfo';
import { ReviewList } from './ReviewList';
import {
  type CoachReviewsCoachFragment,
  useCoachReviewsReviewsForCoachQuery,
} from './__generated-gql-types__/CoachReviews.generated';

const DEFAULT_REVIEWS_COUNT = 4;

interface CoachReviewsProps {
  coach: CoachReviewsCoachFragment;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  hasReviewed?: boolean;
  onViewAllReviews: () => void;
  setReviewModalOpen: (isOpen: boolean) => void;
}

const CoachReviews: React.FC<CoachReviewsProps> = ({
  coach,
  categoryUrn: categoryUrnSort,
  hasReviewed,
  onViewAllReviews,
  setReviewModalOpen,
}) => {
  const {
    data: limitedReviews,
    loading: limitedReviewsLoading,
    error: limitedReviewsError,
    refetch: refetchLimitedReviews,
  } = useCoachReviewsReviewsForCoachQuery({
    variables: {
      id: coach?.id ?? '',
      categoryUrnSort,
      count: DEFAULT_REVIEWS_COUNT,
    },
  });

  return (
    <div className="md:flex md:flex-row md:space-x-20">
      <div className="flex shrink-0 flex-col md:w-80">
        <CoachReviewInfo
          review={coach}
          hasReviewed={hasReviewed}
          onReview={() => setReviewModalOpen(true)}
        />
        {coach.outcomes ? (
          <Outcomes
            outcomes={coach.outcomes}
            coachName={coach.user.firstName}
          />
        ) : null}
      </div>
      {limitedReviewsLoading ? (
        <div className="my-4 w-full text-center">Loading reviews...</div>
      ) : limitedReviewsError ? (
        <Oops
          description="We encountered an error fetching reviews. Please try again."
          retry={refetchLimitedReviews}
        />
      ) : (
        <div className="mt-6 flex w-full flex-col border-t pt-6 md:mt-0 md:border-none md:pt-0">
          <ReviewList
            className="lg:gap-x-20"
            reviews={limitedReviews?.reviewsForCoach.results.slice(0, 4) ?? []}
          />
          {coach.reviewTotal > DEFAULT_REVIEWS_COUNT ? (
            <div className="mt-6 w-full">
              <Button
                label={`See all ${coach.reviewTotal} reviews`}
                buttonColor={ButtonColor.WHITE}
                onClick={onViewAllReviews}
                width={ButtonWidth.AUTO}
                size={ButtonSize.LARGE}
                id="coach_reviews:view_all_reviews"
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CoachReviews;
